import React, { useState, useEffect, useCallback } from "react";
import { Grid, Box, FormControlLabel, Checkbox } from "@mui/material";
import VisibilityOffRoundedIcon from "@material-ui/icons/VisibilityOffRounded";
import VisibilityRoundedIcon from "@material-ui/icons/VisibilityRounded";
import {
  GridFilterModel,
  GridValueFormatterParams,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";

import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import httpClient from "../../lib/httpClient";
import { apiError } from "../../lib/apiError";
import { initialFilterModel, isThereAnyIncompleteFilter } from "../util/tableUtilsTS";

import { isSingleSelectOperators } from "../util/tableUtils";

import {
  filterModelToQueryValue,
  formatCamelCaseToSeparateWords,
  stringFilterOperators,
  tableCellRender,
} from "../util/tableUtils";
import { MiningAssetsMap } from "./MiningAssetsMap";
import { LayerCheckboxes } from "./LayerCheckboxes";

type MiningAssetSelected = MiningAsset & { selected: boolean };

export const MiningAssets = () => {
  const [tableState, setTableState] = useState({
    isLoading: false,
    total: 0,
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [miningAssetsList, setMiningAssetsList] = useState<MiningAssetSelected[]>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [selectedMiningAssets, setSelectedMiningAssets] = useState<MiningAssetSelected[]>([]);
  const [activeLayers, setActiveLayers] = useState<ActiveLayers>({
    assetsLayerIsActive: true,
    concessions: false,
    detectedMines: false,
    deforestations: false,
    indigenous: false,
  });

  const getMiningData = useCallback(async () => {
    setTableState((old) => ({ ...old, isLoading: true }));
    let sortField, sortDirection;

    if (sortModel && Object.keys(sortModel).length) {
      const { field, sort } = sortModel[0];
      sortField = field;
      sortDirection = sort;
    }
    // TODO: check the debounce for filter input
    const filterQuery = filterModelToQueryValue(filterModel);

    try {
      const { data: miningAssetsResponse } = await httpClient().get(
        `/miningAssets?page=${paginationModel.page}&pageSize=${
          paginationModel.pageSize
        }&filters=${filterQuery}&orderBy=${sortField}&orderDirection=${sortDirection}`,
      );

      setMiningAssetsList(miningAssetsResponse.data);
      setTableState((old) => ({
        ...old,
        isLoading: false,
        total: miningAssetsResponse.total,
      }));
    } catch (error) {
      apiError(error);
      setMiningAssetsList([]);

      setTableState((old) => ({
        ...old,
        isLoading: false,
        total: 0,
      }));
    }
  }, [filterModel, sortModel, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    if (!isThereAnyIncompleteFilter(filterModel)) {
      getMiningData();
    }
  }, [filterModel, getMiningData]);

  const onFilterChange = useCallback((aFilterModel: GridFilterModel) => {
    setFilterModel(aFilterModel);
    setSelectedMiningAssets([]);
  }, []);

  const onSortChange = useCallback((aSortModel: GridSortModel) => {
    setSortModel(aSortModel);
  }, []);

  const onCheckboxChange = async (selectedRow: MiningAssetSelected) => {
    const updatedAssetsData = miningAssetsList.map((asset) => {
      if (
        asset.name === selectedRow.name &&
        asset.type === selectedRow.type &&
        asset.country === selectedRow.country &&
        asset.owner === selectedRow.owner
      ) {
        return { ...asset, selected: !selectedRow.selected };
      }
      return { ...asset };
    });

    const selected = updatedAssetsData.filter((asset) => asset.selected);

    setSelectedMiningAssets(selected);
    setMiningAssetsList(updatedAssetsData);
  };

  const miningColumns = [
    {
      field: "selected",
      headerName: "Action",
      sortable: false,
      filterable: false,
      renderCell: (params: { row: MiningAssetSelected }) => {
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <FormControlLabel
              label={""}
              control={
                <Checkbox
                  icon={<VisibilityOffRoundedIcon />}
                  checkedIcon={<VisibilityRoundedIcon />}
                  checked={!!params.row.selected}
                  onChange={() => onCheckboxChange(params.row)}
                  color="primary"
                />
              }
            />
          </div>
        );
      },
    },
    {
      field: "name",
      headerName: "Name",
    },
    {
      field: "country",
      headerName: "Country",
    },
    {
      field: "type",
      headerName: "Type",
      type: "singleSelect",
      filterOperators: isSingleSelectOperators,
      valueOptions: [
        { value: "coalMine", label: formatCamelCaseToSeparateWords("coalMine") },
        { value: "coalPlant", label: formatCamelCaseToSeparateWords("coalPlant") },
        { value: "coalTerminal", label: formatCamelCaseToSeparateWords("coalTerminal") },
        { value: "ironOreMine", label: formatCamelCaseToSeparateWords("ironOreMine") },
      ],
      valueGetter: (params: TableRowData<MiningAssetSelected>) =>
        params.row.type && formatCamelCaseToSeparateWords(params.row.type),
    },
    {
      field: "owner",
      headerName: "Owner",
    },
  ];

  const columns = miningColumns.map((tableColumn) => ({
    valueFormatter: (params: GridValueFormatterParams) => tableCellRender(params.value?.toString()),
    filterOperators: stringFilterOperators,
    flex: 1,
    ...tableColumn,
  }));

  const goToMineAssetPage = (mineAssetId: number) => {
    const wnd = window.open(`#/mining/${mineAssetId}`, "_blank");
    if (!wnd) return;

    wnd.onload = function () {
      wnd.document.title = `${mineAssetId}`;
    };
  };

  return (
    <Box mt={3}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={12}>
          <MiningAssetsMap
            miningAssets={selectedMiningAssets}
            filterModel={filterModel}
            activeLayers={activeLayers}
          ></MiningAssetsMap>
        </Grid>
        <Grid item xs={12} sm={12}>
          <LayerCheckboxes activeLayers={activeLayers} setActiveLayers={setActiveLayers} />
        </Grid>

        <Grid item xs={12} sm={12}>
          <DataGridCystellarTable
            columns={columns}
            rows={miningAssetsList}
            rowCount={tableState.total}
            getRowId={(rowData: TableRowData<MiningAsset>) => rowData._id}
            loading={tableState.isLoading}
            paginationMode={"server"}
            paginationModel={paginationModel}
            pageSizeOptions={[10, 20, 50]}
            onPaginationModelChange={(newPaginationModel: GridPaginationModel) =>
              setPaginationModel(newPaginationModel)
            }
            filterMode={"server"}
            onFilterModelChange={onFilterChange}
            sortingMode={"server"}
            onSortModelChange={onSortChange}
            onRowClick={(rowData: TableRowData<MiningAsset>) => goToMineAssetPage(rowData.row.mineAssetId)}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
